import React, { useEffect, useState } from "react";
import { Send, Plus, ArrowRight, FileText } from "lucide-react";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../../../components/ui/select";
import CatalogTable from "../components/CatalogTable";
import { CatalogLoadingCard, CatalogErrorCard } from "../components/CatalogStateCard";
import EntityFormDialog from "../components/EntityFormDialog";
import MutationErrorAlert from "../components/MutationErrorAlert";
import StatusBadge from "../components/StatusBadge";
import AssetVersionSlotField from "../components/AssetVersionSlotField";
import BroadcastHandoffView from "./BroadcastHandoffView";
import {
  legalNextStatuses,
  isEligibleForHandoffGeneration,
  isLaunchReportedOfferable,
  evaluateBroadcastLaunchReadiness,
  BROADCAST_ENVIRONMENT_VALUES,
} from "./broadcastStatusTransitions";
import { useEmailCatalogEntityList } from "../../../hooks/useEmailCatalogEntityList";
import {
  getEmailMarketingCampaigns,
  getEmailMarketingAudienceDefinitions,
  getCreatives,
  getCreativeVersions,
  getSenderConfigurations,
  getFooterConfigurations,
  getBroadcastAuthoringList,
  createBroadcastAuthoring,
  updateBroadcastAuthoring,
  generateBroadcastHandoffSnapshot,
  describeMutationError,
} from "../../../lib/emailMarketingApi";

/**
 * BroadcastsPanel — EMAILS-IMPL-020
 *
 * Governed Broadcast authoring: browse the operational catalog
 * (email_marketing_broadcast_authoring.py's `emails: full`-gated
 * `/broadcast-authoring` routes -- never the pre-existing War-Room-gated
 * `/broadcasts` analytics rollup, which a real frontend consumer,
 * EmailCreativeVersionTable.jsx, already depends on and this checkpoint
 * does not touch), create a Broadcast pinned to an exact Campaign/
 * Creative Version/Audience Definition/Environment, progress it through
 * the governed prospective lifecycle (EMAILS-IMPL-024, BRD-SPEC-001
 * v2.6.0 §21), and generate its one-time immutable Rendered Handoff
 * Snapshot for manual GHL recreation. "Launch Reported" is an explicit
 * operator ASSERTION that the manual GHL launch was performed -- never a
 * claim MissionAssist verified GHL/SendGrid execution; see the dedicated
 * "Manual GHL Launch" affordance below, deliberately separate from the
 * generic Status Transitions buttons.
 *
 * Broadcast identity discipline (checkpoint §6): a Broadcast is one
 * governed EXECUTION, never editable back into a different Campaign/
 * Creative/Version/Audience/Environment once created (PATCH is metadata-
 * only -- name/status). A materially different execution is always a NEW
 * Broadcast, never a mutation of an existing one -- this panel never
 * offers a "resend" or "duplicate into same BRC" action.
 */

// Creative eligibility gate mirrors email_marketing_broadcast_authoring_
// service.py's own `_CREATIVE_ELIGIBLE_STATUSES` exactly (Approved or
// Control) -- an affordance filter only; the backend independently
// re-validates on submit.
const CREATIVE_ELIGIBLE_STATUSES = new Set(["Approved", "Control"]);

function emptyCreateForm() {
  return {
    name: "", campaignId: "", creative: null, audienceDefinitionId: "", environment: "",
    senderConfigurationId: "", footerConfigurationId: "",
  };
}

// BRC0026-PAGINATION-FIX: the Broadcast authoring catalog is an unbounded
// historical operational record served by the ONLY server-bounded list
// endpoint in this Emails catalog family. It is therefore paginated
// newest-first rather than assumed to arrive whole -- the previous
// assumption silently hid every Broadcast past the first 25.
const BROADCAST_PAGE_SIZE = 25;

export default function BroadcastsPanel() {
  const [offset, setOffset] = useState(0);
  const listArgs = { limit: BROADCAST_PAGE_SIZE, offset };
  const {
    items, loading, error, mutating, mutationError, clearMutationError,
    createItem, updateItem, page,
  } = useEmailCatalogEntityList({
      list: getBroadcastAuthoringList,
      create: createBroadcastAuthoring,
      update: updateBroadcastAuthoring,
      getId: (row) => row.broadcast_id,
      listArgs,
      // The server owns newest-first ordering, so a created Broadcast is
      // surfaced by refetching page 0 rather than appended to the end.
      refetchAfterCreate: true,
    });

  const total = page?.total ?? items.length;
  const effectiveLimit = page?.limit ?? BROADCAST_PAGE_SIZE;
  const effectiveOffset = page?.offset ?? 0;
  const hasOlder = effectiveOffset + items.length < total;
  const hasNewer = effectiveOffset > 0;

  // Reference data for list/detail display (name lookups) and the Create
  // form's Campaign/Audience Definition selects -- Creative's own
  // list+exact-Version selection is handled entirely by
  // AssetVersionSlotField below (it fetches Creatives + Version history
  // itself); this panel fetches Creatives separately, ONLY for the
  // name-lookup used to label existing rows, mirroring
  // CampaignsPanel/CreativesPanel's own established reference-fetch
  // pattern rather than modifying the shared, already-established
  // AssetVersionSlotField component to expose its internal fetch.
  const [campaigns, setCampaigns] = useState([]);
  const [creatives, setCreatives] = useState([]);
  const [audiences, setAudiences] = useState([]);
  const [senders, setSenders] = useState([]);
  const [footers, setFooters] = useState([]);
  const [refLoading, setRefLoading] = useState(true);
  const [refError, setRefError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [c, cr, a, s, f] = await Promise.all([
          getEmailMarketingCampaigns(),
          getCreatives(),
          getEmailMarketingAudienceDefinitions(),
          getSenderConfigurations(),
          getFooterConfigurations(),
        ]);
        if (!cancelled) {
          setCampaigns(c || []);
          setCreatives(cr || []);
          setAudiences(a || []);
          setSenders(s || []);
          setFooters(f || []);
        }
      } catch {
        if (!cancelled)
          setRefError(
            "Failed to load Campaigns/Creatives/Audience Definitions/Sender Configurations/Footer Configurations for the Broadcast form."
          );
      } finally {
        if (!cancelled) setRefLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const campaignName = (id) => campaigns.find((c) => c.campaign_id === id)?.name;
  const creativeName = (id) => creatives.find((c) => c.creative_id === id)?.name;
  const senderName = (id) => senders.find((s) => s.id === id)?.name;
  const footerName = (id) => footers.find((f) => f.id === id)?.name;
  // EMAILS-IMPL-027: the Creative's own recorded Campaign is provenance/
  // origin context only (CRE-SPEC-001 §23.8) -- never used to filter or
  // gate the Creative selector above, only to label it informationally.
  const creativeCampaignId = (creativeAssetId) => creatives.find((c) => c.creative_id === creativeAssetId)?.campaign_id;
  const creativeCampaignName = (creativeAssetId) => {
    const cid = creativeCampaignId(creativeAssetId);
    if (!cid) return null;
    const name = campaignName(cid);
    return name ? `${name} (${cid})` : cid;
  };
  // Only Approved configurations for the Broadcast's own selected
  // Environment are ever selectable -- BRD-SPEC-001 §10.2 rules 3/4: an
  // operator never types sender values, and a Draft/Retired/wrong-
  // Environment configuration must never even be offered as an option.
  const eligibleSenders = (environment) =>
    senders.filter((s) => s.status === "Approved" && s.environment === environment);
  // EMAILS-IMPL-033: identical eligibility contract for Footer
  // Configuration -- BRD-SPEC-001 §10.3 rules mirror §10.2's Sender
  // Configuration rules exactly.
  const eligibleFooters = (environment) =>
    footers.filter((f) => f.status === "Approved" && f.environment === environment);

  const [expandedId, setExpandedId] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState(emptyCreateForm());

  const openCreate = () => {
    setCreateForm(emptyCreateForm());
    clearMutationError();
    setCreateOpen(true);
  };

  const submitCreate = async () => {
    try {
      // BRC0026-PAGINATION-FIX: a newly created Broadcast is the NEWEST, so
      // under server-authoritative newest-first ordering it lives on the
      // first page. Return there before the refetch, otherwise an operator
      // creating a Broadcast while viewing an older page would refetch that
      // older page and correctly -- but confusingly -- not see it.
      setOffset(0);
      await createItem({
        name: createForm.name.trim(),
        campaignId: createForm.campaignId,
        creativeId: createForm.creative?.assetId,
        creativeVersion: createForm.creative?.version,
        audienceDefinitionId: createForm.audienceDefinitionId,
        environment: createForm.environment,
        senderConfigurationId: createForm.senderConfigurationId,
        footerConfigurationId: createForm.footerConfigurationId,
      });
      setCreateOpen(false);
    } catch {
      // handled via mutationError
    }
  };

  // Status transitions: reuses the SAME updateItem/mutating/mutationError
  // the create/edit flows already share (email_marketing_broadcast_
  // authoring.py's PATCH endpoint is the single mutation surface for both
  // name and status). Only legal next statuses are ever offered as
  // buttons -- see broadcastStatusTransitions.js's own module docstring
  // for why the backend remains the sole authority regardless.
  const [transitionError, setTransitionError] = useState(null);
  const requestTransition = async (broadcastId, nextStatus) => {
    setTransitionError(null);
    try {
      await updateItem(broadcastId, { status: nextStatus });
    } catch (err) {
      setTransitionError(describeMutationError(err, "Failed to update status."));
    }
  };

  // Handoff Snapshot generation: a distinct action from ordinary
  // Broadcast CRUD, with its own mutating/error state -- the POST .../
  // handoff-snapshot endpoint returns a RenderedHandoffSnapshot, not a
  // full BroadcastResponse, so its result is merged into the existing
  // local row rather than reused via useEmailCatalogEntityList's own
  // create/update upsert helpers (which expect a full replacement row).
  const [handoffMutatingId, setHandoffMutatingId] = useState(null);
  const [handoffError, setHandoffError] = useState(null);
  const [handoffView, setHandoffView] = useState(null); // { broadcast, snapshot } | null
  const [localSnapshots, setLocalSnapshots] = useState({}); // broadcastId -> RenderedHandoffSnapshot

  const generateHandoff = async (broadcast) => {
    setHandoffError(null);
    setHandoffMutatingId(broadcast.broadcast_id);
    try {
      const snapshot = await generateBroadcastHandoffSnapshot(broadcast.broadcast_id);
      setLocalSnapshots((prev) => ({ ...prev, [broadcast.broadcast_id]: snapshot }));
      setHandoffView({ broadcast, snapshot });
    } catch (err) {
      setHandoffError(describeMutationError(err, "Failed to generate the GHL Handoff Snapshot."));
    } finally {
      setHandoffMutatingId(null);
    }
  };

  const openExistingHandoff = (broadcast, snapshot) => {
    setHandoffView({ broadcast, snapshot });
  };

  const snapshotFor = (broadcast) => localSnapshots[broadcast.broadcast_id] || broadcast.rendered_handoff_snapshot;

  if (loading || refLoading) return <CatalogLoadingCard testId="broadcasts-loading" />;
  if (error) return <CatalogErrorCard message={error} testId="broadcasts-error" />;
  if (refError) return <CatalogErrorCard message={refError} testId="broadcasts-ref-error" />;

  const noCampaigns = campaigns.length === 0;
  const noAudiences = audiences.length === 0;
  const creationBlocked = noCampaigns || noAudiences;

  return (
    <div className="space-y-4" data-testid="broadcasts-panel">
      <CatalogTable
        testId="broadcasts-table"
        title="Broadcasts"
        icon={Send}
        headerAction={
          <Button
            type="button"
            size="sm"
            className="gap-1.5 bg-violet-600 text-white hover:bg-violet-500"
            onClick={openCreate}
            disabled={creationBlocked}
            title={creationBlocked ? "Create a Campaign and an Audience Definition first" : undefined}
            data-testid="new-broadcast-button"
          >
            <Plus className="h-4 w-4" /> New Broadcast
          </Button>
        }
        rows={items}
        getRowKey={(row) => row.broadcast_id}
        onRowClick={(row) => setExpandedId(expandedId === row.broadcast_id ? null : row.broadcast_id)}
        emptyTitle="No Broadcasts yet"
        emptySubtitle={creationBlocked ? "Create a Campaign and an Audience Definition first, then create a Broadcast." : 'Create one with "New Broadcast" to get started.'}
        columns={[
          {
            key: "name",
            label: "Broadcast",
            render: (row) => (
              <div>
                <div className="text-sm font-medium text-slate-100">{row.name}</div>
                <div className="text-xs font-mono text-slate-400">{row.broadcast_id}</div>
              </div>
            ),
          },
          {
            key: "campaign",
            label: "Campaign",
            render: (row) => <span className="text-sm text-slate-300">{campaignName(row.campaign_id) || row.campaign_id}</span>,
          },
          {
            key: "creative",
            label: "Creative",
            render: (row) => (
              <span className="text-sm text-slate-300">
                {creativeName(row.creative_id) || row.creative_id}{" "}
                <span className="font-mono text-xs text-slate-500">
                  ({row.creative_id} v{row.creative_version})
                </span>
              </span>
            ),
          },
          {
            key: "audience",
            label: "Audience",
            render: (row) => <span className="text-sm text-slate-300">{(row.target_audience_names || [])[0] || "—"}</span>,
          },
          {
            key: "environment",
            label: "Environment",
            render: (row) =>
              row.environment ? (
                <span
                  className={`text-xs font-semibold uppercase tracking-wide ${
                    row.environment === "Production" ? "text-rose-300" : "text-sky-300"
                  }`}
                  data-testid={`broadcast-environment-${row.broadcast_id}`}
                >
                  {row.environment}
                </span>
              ) : (
                <span className="text-sm text-slate-500">—</span>
              ),
          },
          { key: "status", label: "Status", render: (row) => <StatusBadge status={row.status} testId={`broadcast-status-${row.broadcast_id}`} /> },
          {
            key: "readiness",
            label: "Launch Readiness",
            render: (row) => (
              <ReadinessBadge
                readiness={evaluateBroadcastLaunchReadiness({ ...row, rendered_handoff_snapshot: snapshotFor(row) })}
                testId={`broadcast-readiness-${row.broadcast_id}`}
              />
            ),
          },
        ]}
      />

      {/* BRC0026-PAGINATION-FIX: the operator must be able to see that more
          Broadcasts exist and reach them. Mirrors the existing MissionAssist
          pagination control (OutboundEventsViewer) rather than inventing a
          new one. Rendered whenever the catalog exceeds one page, so a small
          tenant sees no new chrome. */}
      {total > items.length || effectiveOffset > 0 ? (
        <div
          className="flex items-center justify-between mt-4 pt-4 border-t border-slate-800"
          data-testid="broadcast-pagination"
        >
          <span className="text-sm text-slate-400" data-testid="broadcast-pagination-summary">
            Showing {items.length === 0 ? 0 : effectiveOffset + 1}
            {" - "}
            {effectiveOffset + items.length} of {total} — newest first
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={!hasNewer || loading}
              onClick={() => setOffset(Math.max(0, effectiveOffset - effectiveLimit))}
              className="border-slate-700"
              data-testid="broadcast-page-newer"
            >
              Newer
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={!hasOlder || loading}
              onClick={() => setOffset(effectiveOffset + effectiveLimit)}
              className="border-slate-700"
              data-testid="broadcast-page-older"
            >
              Older
            </Button>
          </div>
        </div>
      ) : null}

      {items
        .filter((row) => row.broadcast_id === expandedId)
        .map((row) => {
          const snapshot = snapshotFor(row);
          const rowForReadiness = { ...row, rendered_handoff_snapshot: snapshot };
          const eligibleForHandoff = isEligibleForHandoffGeneration(rowForReadiness);
          const readiness = evaluateBroadcastLaunchReadiness(rowForReadiness);
          // "Launch Reported" is deliberately excluded from the generic
          // Status Transitions button list below -- it gets its own
          // dedicated, explicitly-worded operator-action affordance (the
          // "Manual GHL Launch" block further down), never a bare status
          // button, per EMAILS-IMPL-024's explicit UX requirement.
          const nextStatuses = legalNextStatuses(row.status).filter((s) => s !== "Launch Reported");
          const launchReportedOfferable = isLaunchReportedOfferable(rowForReadiness);
          // Case 4 (Not Eligible for Handoff): the frontend already knows,
          // from fields already on the response, that generation would
          // fail closed -- offering the button anyway would be a
          // guaranteed-fail affordance. Every OTHER Approved/Scheduled,
          // no-Snapshot case still offers it (case 2), including the deep
          // Creative/Landing-Page validation failures this module
          // deliberately cannot predict -- those remain surfaced honestly
          // by the existing post-attempt 422 message below.
          const offerGenerateAction = eligibleForHandoff && readiness.state !== "not_eligible";
          return (
            <div
              key={row.broadcast_id}
              className="space-y-4 rounded-lg border border-white/10 bg-white/5 p-4"
              data-testid={`broadcast-detail-${row.broadcast_id}`}
            >
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm sm:grid-cols-3">
                <Detail label="Broadcast ID" value={row.broadcast_id} mono />
                <Detail label="Status" value={<StatusBadge status={row.status} testId={`broadcast-detail-status-${row.broadcast_id}`} />} />
                <Detail label="Environment" value={row.environment || "—"} />
                <Detail label="Campaign" value={`${row.campaign_id}${campaignName(row.campaign_id) ? ` — ${campaignName(row.campaign_id)}` : ""}`} />
                <Detail label="Audience Definition" value={(row.target_audience_names || [])[0] || "—"} />
                <Detail label="Creative" value={`${row.creative_id}${creativeName(row.creative_id) ? ` — ${creativeName(row.creative_id)}` : ""}`} />
                <Detail label="Exact Creative Version" value={row.creative_version || "—"} mono />
                <Detail label="Sender Configuration" value={row.sender_configuration_id ? senderName(row.sender_configuration_id) || row.sender_configuration_id : "—"} />
                <Detail label="Footer Configuration" value={row.footer_configuration_id ? footerName(row.footer_configuration_id) || row.footer_configuration_id : "—"} />
                <Detail label="Created" value={row.created_at ? new Date(row.created_at).toLocaleString() : "—"} />
                <Detail label="Updated" value={row.updated_at ? new Date(row.updated_at).toLocaleString() : "—"} />
              </div>

              <div className="space-y-1.5 border-t border-white/10 pt-3" data-testid={`broadcast-readiness-detail-${row.broadcast_id}`}>
                <div className="text-xs uppercase tracking-wide text-slate-500">Launch Readiness</div>
                <ReadinessBadge readiness={readiness} testId={`broadcast-readiness-detail-badge-${row.broadcast_id}`} />
                {readiness.detail && (
                  <p className="text-xs text-slate-400" data-testid={`broadcast-readiness-detail-text-${row.broadcast_id}`}>
                    {readiness.detail}
                  </p>
                )}
                {readiness.reasons && readiness.reasons.length > 0 && (
                  <ul className="list-disc space-y-0.5 pl-4 text-xs text-amber-300" data-testid={`broadcast-readiness-reasons-${row.broadcast_id}`}>
                    {readiness.reasons.map((reason) => (
                      <li key={reason}>{reason}</li>
                    ))}
                  </ul>
                )}
              </div>

              {nextStatuses.length > 0 && (
                <div className="space-y-2 border-t border-white/10 pt-3">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Status Transitions</div>
                  <div className="flex flex-wrap gap-2">
                    {nextStatuses.map((next) => (
                      <Button
                        key={next}
                        type="button"
                        size="sm"
                        variant="outline"
                        className="gap-1.5 border-white/10 text-slate-200"
                        disabled={mutating}
                        onClick={() => requestTransition(row.broadcast_id, next)}
                        data-testid={`broadcast-transition-${row.broadcast_id}-${next}`}
                      >
                        <ArrowRight className="h-3.5 w-3.5" /> {next}
                      </Button>
                    ))}
                  </div>
                  {transitionError && <MutationErrorAlert error={transitionError} testId={`broadcast-transition-error-${row.broadcast_id}`} />}
                </div>
              )}

              <div className="space-y-2 border-t border-white/10 pt-3">
                <div className="text-xs uppercase tracking-wide text-slate-500">GHL Handoff</div>
                {snapshot ? (
                  <Button
                    type="button"
                    size="sm"
                    className="gap-1.5 bg-violet-600 text-white hover:bg-violet-500"
                    onClick={() => openExistingHandoff(row, snapshot)}
                    data-testid={`open-handoff-${row.broadcast_id}`}
                  >
                    <FileText className="h-4 w-4" /> Open GHL Handoff
                  </Button>
                ) : offerGenerateAction ? (
                  <Button
                    type="button"
                    size="sm"
                    className="gap-1.5 bg-violet-600 text-white hover:bg-violet-500"
                    disabled={handoffMutatingId === row.broadcast_id}
                    onClick={() => generateHandoff(row)}
                    data-testid={`generate-handoff-${row.broadcast_id}`}
                  >
                    <FileText className="h-4 w-4" />
                    {handoffMutatingId === row.broadcast_id ? "Generating…" : "Generate GHL Handoff"}
                  </Button>
                ) : readiness.state === "not_eligible" ? (
                  <p className="text-xs text-slate-500" data-testid={`handoff-unavailable-${row.broadcast_id}`}>
                    Not Eligible for Handoff -- see Launch Readiness above.
                  </p>
                ) : (
                  <p className="text-xs text-slate-500" data-testid={`handoff-unavailable-${row.broadcast_id}`}>
                    A Handoff Snapshot can be generated once this Broadcast is Approved or Scheduled.
                  </p>
                )}
                {handoffError && handoffMutatingId === null && (
                  <MutationErrorAlert error={handoffError} testId={`handoff-error-${row.broadcast_id}`} />
                )}
              </div>

              {/* EMAILS-IMPL-024, BRD-SPEC-001 §21.1/§21.2: an explicit
                  operator ACTION, never a bare "Launch Reported" status
                  button -- the copy below must read as the operator's own
                  report of what THEY did in GHL, never as MissionAssist
                  confirming delivery/sending/provider acceptance. The
                  backend independently re-validates both the Approved/
                  Scheduled + Snapshot-exists precondition and remains
                  authoritative regardless of this frontend gate. */}
              {row.status === "Launch Reported" ? (
                <div className="space-y-1 border-t border-white/10 pt-3" data-testid={`launch-reported-note-${row.broadcast_id}`}>
                  <div className="text-xs uppercase tracking-wide text-slate-500">Manual GHL Launch</div>
                  <p className="text-xs text-slate-400">
                    Manual GHL launch reported by the operator. This is an operator assertion — it does not confirm SendGrid/GHL
                    delivery, and it is not evidence of what actually happened downstream.
                  </p>
                </div>
              ) : launchReportedOfferable ? (
                <div className="space-y-2 border-t border-white/10 pt-3">
                  <div className="text-xs uppercase tracking-wide text-slate-500">Manual GHL Launch</div>
                  <p className="text-xs text-slate-400">
                    Once you have manually recreated and launched this Broadcast in GHL, record that here. This is your own
                    report — MissionAssist cannot verify GHL or SendGrid execution.
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="gap-1.5 border-blue-400/30 text-blue-300"
                    disabled={mutating}
                    onClick={() => requestTransition(row.broadcast_id, "Launch Reported")}
                    data-testid={`report-launch-${row.broadcast_id}`}
                  >
                    <ArrowRight className="h-3.5 w-3.5" /> Report Manual GHL Launch
                  </Button>
                  {/* transitionError is rendered once, in the Status
                      Transitions block above -- launchReportedOfferable is
                      only ever true when status is Approved/Scheduled, and
                      for both of those Cancelled always remains a legal
                      next status after filtering out "Launch Reported", so
                      that block is always present alongside this one;
                      rendering the SAME error a second time here (same
                      data-testid) would break single-match test queries. */}
                </div>
              ) : null}
            </div>
          );
        })}

      <EntityFormDialog
        testId="create-broadcast-dialog"
        open={createOpen}
        onOpenChange={setCreateOpen}
        title="New Broadcast"
        description="A Broadcast pins one governed execution to an exact Campaign, Creative Version, Audience Definition, and Environment."
        onSubmit={submitCreate}
        submitting={mutating}
        error={mutationError}
        submitLabel="Create Broadcast"
      >
        <div className="space-y-2">
          <Label htmlFor="broadcast-name">Name</Label>
          <Input
            id="broadcast-name"
            value={createForm.name}
            onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Q1 Affordability Launch"
            required
            data-testid="broadcast-name-input"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="broadcast-campaign-select">Campaign</Label>
          <Select value={createForm.campaignId} onValueChange={(v) => setCreateForm((f) => ({ ...f, campaignId: v }))}>
            <SelectTrigger id="broadcast-campaign-select" data-testid="broadcast-campaign-select">
              <SelectValue placeholder="Select a Campaign" />
            </SelectTrigger>
            <SelectContent>
              {campaigns.map((c) => (
                <SelectItem key={c.campaign_id} value={c.campaign_id} data-testid={`broadcast-campaign-option-${c.campaign_id}`}>
                  {c.name} ({c.campaign_id})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <AssetVersionSlotField
          label="Creative"
          required
          value={createForm.creative}
          onChange={(v) => setCreateForm((f) => ({ ...f, creative: v }))}
          listFn={getCreatives}
          versionsFn={getCreativeVersions}
          idField="creative_id"
          eligibleStatuses={CREATIVE_ELIGIBLE_STATUSES}
          testPrefix="broadcast-creative"
        />
        {/* EMAILS-IMPL-027: a Creative is reusable across any number of
            Campaigns -- its own recorded Campaign is provenance/origin
            context only, never an eligibility restriction, so every
            eligible Creative is offered here regardless of the Campaign
            selected above. This line is purely informational (secondary,
            non-blocking) and never implies incompatibility. */}
        {createForm.creative?.assetId && (
          <p className="-mt-1 text-xs text-slate-500" data-testid="broadcast-creative-provenance">
            Originally created under: {creativeCampaignName(createForm.creative.assetId) || "—"}
          </p>
        )}

        <div className="space-y-2">
          <Label htmlFor="broadcast-audience-select">Audience Definition</Label>
          <Select
            value={createForm.audienceDefinitionId}
            onValueChange={(v) => setCreateForm((f) => ({ ...f, audienceDefinitionId: v }))}
          >
            <SelectTrigger id="broadcast-audience-select" data-testid="broadcast-audience-select">
              <SelectValue placeholder="Select an Audience Definition" />
            </SelectTrigger>
            <SelectContent>
              {audiences.map((a) => (
                <SelectItem key={a.audience_id} value={a.audience_id} data-testid={`broadcast-audience-option-${a.audience_id}`}>
                  {a.name} ({a.audience_id})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-500">The intended, targeted Audience Definition — not a claim of delivery.</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="broadcast-environment-select">Environment</Label>
          <Select
            value={createForm.environment}
            onValueChange={(v) =>
              setCreateForm((f) => ({
                ...f,
                environment: v,
                // Changing Environment invalidates any already-selected
                // Sender/Footer Configuration approved for the OLD
                // Environment -- clear both rather than silently
                // submitting a mismatched pairing the backend would
                // reject anyway.
                senderConfigurationId: "",
                footerConfigurationId: "",
              }))
            }
          >
            <SelectTrigger id="broadcast-environment-select" data-testid="broadcast-environment-select">
              <SelectValue placeholder="Select an Environment" />
            </SelectTrigger>
            <SelectContent>
              {BROADCAST_ENVIRONMENT_VALUES.map((env) => (
                <SelectItem key={env} value={env} data-testid={`broadcast-environment-option-${env}`}>
                  {env}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="broadcast-sender-select">Sender Configuration</Label>
          <Select
            value={createForm.senderConfigurationId}
            onValueChange={(v) => setCreateForm((f) => ({ ...f, senderConfigurationId: v }))}
            disabled={!createForm.environment}
          >
            <SelectTrigger id="broadcast-sender-select" data-testid="broadcast-sender-select">
              <SelectValue placeholder={createForm.environment ? "Select a Sender Configuration" : "Select an Environment first"} />
            </SelectTrigger>
            <SelectContent>
              {eligibleSenders(createForm.environment).map((s) => (
                <SelectItem key={s.id} value={s.id} data-testid={`broadcast-sender-option-${s.id}`}>
                  {s.name} ({s.from_address})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {createForm.environment && eligibleSenders(createForm.environment).length === 0 && (
            <p className="text-xs text-amber-300" data-testid="broadcast-sender-none-available">
              No Approved Sender Configuration exists for {createForm.environment}. Approve one in the Senders tab first.
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="broadcast-footer-select">Footer Configuration</Label>
          <Select
            value={createForm.footerConfigurationId}
            onValueChange={(v) => setCreateForm((f) => ({ ...f, footerConfigurationId: v }))}
            disabled={!createForm.environment}
          >
            <SelectTrigger id="broadcast-footer-select" data-testid="broadcast-footer-select">
              <SelectValue placeholder={createForm.environment ? "Select a Footer Configuration" : "Select an Environment first"} />
            </SelectTrigger>
            <SelectContent>
              {eligibleFooters(createForm.environment).map((f) => (
                <SelectItem key={f.id} value={f.id} data-testid={`broadcast-footer-option-${f.id}`}>
                  {f.name} ({f.brand_name})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {createForm.environment && eligibleFooters(createForm.environment).length === 0 && (
            <p className="text-xs text-amber-300" data-testid="broadcast-footer-none-available">
              No Approved Footer Configuration exists for {createForm.environment}. Approve one in the Footers tab first.
            </p>
          )}
        </div>
      </EntityFormDialog>

      {handoffView && (
        <BroadcastHandoffView
          open={!!handoffView}
          onOpenChange={(open) => !open && setHandoffView(null)}
          broadcast={handoffView.broadcast}
          snapshot={handoffView.snapshot}
        />
      )}
    </div>
  );
}

// EMAILS-IMPL-022: purely presentational -- color/label only, never its
// own eligibility logic. Every color choice is neutral or cautionary
// (violet/amber/slate), deliberately never a delivery-confirmation-style
// green "success" treatment for the terminal-lifecycle "Handoff Generated"
// state, since that state is historical Handoff evidence, not proof of
// anything GHL/EDT observed.
const READINESS_BADGE_STYLES = {
  launch_ready: "border-violet-400/40 bg-violet-500/15 text-violet-200",
  handoff_not_generated: "border-sky-400/40 bg-sky-500/15 text-sky-200",
  approval_not_complete: "border-white/10 bg-white/5 text-slate-300",
  not_eligible: "border-amber-400/30 bg-amber-500/10 text-amber-300",
  cancelled: "border-white/10 bg-white/5 text-slate-400",
  not_launch_ready: "border-white/10 bg-white/5 text-slate-400",
  handoff_generated_historical: "border-white/10 bg-white/5 text-slate-300",
};

function ReadinessBadge({ readiness, testId }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${READINESS_BADGE_STYLES[readiness.state] || READINESS_BADGE_STYLES.not_launch_ready}`}
      data-testid={testId}
    >
      {readiness.label}
    </span>
  );
}

function Detail({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
      <div className={mono ? "font-mono text-slate-200" : "text-slate-200"}>{value}</div>
    </div>
  );
}
